import React, { useCallback, useEffect, useState } from 'react';
import './Documents.scss';
import { DashboardLayout } from '../layouts/dashboard-layout/DashboardLayout';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../app/store';
import DocumentsView from '../features/documents-view/DocumentsView';
import { UserDisplay } from '../features/user-display/UserDisplay';
import { Api } from '../api';
import { DossierMember } from '../models/DossierMember';
import { useHistory, useLocation } from 'react-router-dom';
import { ISplitLayoutItem, SplitSubLayout } from '../layouts/split-sub-layouts/SplitSubLayout';
import { ERouterPath } from '../features/router/ERouterPath';
import { Icon } from '../features/icon/Icon';
import { ISessionAppState } from '../app/session/reducer';
import { UserRelationRoleEnum } from '../api/types';

function Documents(): JSX.Element {
  const { dossier, relation: dossierRelation, user } = useSelector<ApplicationState, ISessionAppState>(state => state.session);
  const history = useHistory();
  const location = useLocation();
  const [selectedMember, setSelectedMember] = useState<DossierMember|undefined>(undefined);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState<string|undefined>(undefined);
  const [members, setMembers] = useState<DossierMember[]>([]);
  const [splitLayoutSidebarItems, setSplitLayoutSidebarItems] = useState<ISplitLayoutItem[]>([]);
  const [isActive, setIsActive] = useState<boolean>(false);

  const handleSelectMember = useCallback((member: DossierMember) => {
    setSelectedMember(member);
    setIsActive(true);
  }, []);

  const getRouterPathToMember = (memberId: number) => ERouterPath.documentsOfUser.replace(':dossierMemberId', `${memberId}`);

  useEffect(() => {
    // This is legacy support, the backend will still redirect to `/documents?relation=3` instead of /documents/3`
    // therefor we will still listen to this get-variable.
    const query = location.search;
    if (!query) {
      return;
    }

    for (const substring of query.split('relation=')) {
      const id = +substring;
      if (!isNaN(id)) {
        history.replace(getRouterPathToMember(id));
      }
    }
  }, [location, history]);

  useEffect(() => {
    // Load members
    if (!dossier) {
      console.error('Dossier not set');
      setLoadingError('Laden mislukt, omdat er geen dossier geselecteerd is.');
      setLoadingMembers(false);
      return;
    }

    setLoadingMembers(true);
    setLoadingError(undefined);

    Api.listDossierRelations(dossier.id)
      .then(members => {
        const isCaregiver = dossierRelation?.role === UserRelationRoleEnum.caregiver;
        if (user && isCaregiver) {
          // Remove current user from list and other caregivers
          members = members.filter(member => {
            const isNotThisUser = member.user.id !== user.id;
            const isNotCaregiver = member.role !== UserRelationRoleEnum.caregiver;
            return isNotThisUser && isNotCaregiver;
          });
        } else if (user) {
          // Get only current user from list
          members = members.filter((member) => member.user.id === user.id);
        }
        setMembers(members);
      })
      .catch(err => {
        console.error('Failed to load dossier relations', err);
        setLoadingError('Laden mislukt door een onverwachte fout');
      })
      .finally(() => setLoadingMembers(false));
  }, [dossier, dossierRelation, user]);

  useEffect(() => {
    // Create the sidebar items
    const items: ISplitLayoutItem[] = [];
    const defaults = { className: 'documents__column-item' };
    if (loadingError) {
      items.push({
        ...defaults,
        element: <Alert variant="danger">{loadingError}</Alert>,
      });
    }
    if (loadingMembers) {
      items.push({
        ...defaults,
        element: <Spinner animation="border" variant="primary" />,
      });
      return;
    }

    const memberItems: ISplitLayoutItem[] = members.map(member => ({
      ...defaults,
      routerPath: getRouterPathToMember(member.memberId),
      element: <UserDisplay user={member.user} subTitle={member.getRoleText({ capitalize: true })} small={true} />,
      mobileBackButtonElement: <div className="documents-back-button" onClick={() => setIsActive(false)}>
        <Icon className="documents-back-button__back-icon" icon="back" />
        <UserDisplay className="documents-back-button__element" user={member.user} subTitle={member.getRoleText({ capitalize: true })} small={true} />
      </div>,
      onSelect: () => handleSelectMember(member),
    }));

    setSplitLayoutSidebarItems([...items, ...memberItems]);
  }, [members, loadingMembers, loadingError, handleSelectMember]);

  return <DashboardLayout className="documents__container" grayBackgroundMobile={isActive}>
    <h1 className="page-title">Materialen</h1>
    <SplitSubLayout items={splitLayoutSidebarItems} rootRouterPath={ERouterPath.documents} hideSidebarAndBackButton={splitLayoutSidebarItems.length === 1}>
      { selectedMember ? <DocumentsView member={selectedMember} /> : <Alert className="documents__info" variant="light">Er is nog geen gebruiker geselecteerd om de materialen van te zien</Alert> }
    </SplitSubLayout>
  </DashboardLayout>;
}

export default Documents;
